/* site/cms/builder/store.js — the builder's single source of truth (zustand, 2026-09-12).
 *
 * Owns: the page list, the OPEN page (its draft tree is what the canvas edits), selection,
 * device preview, undo/redo history (snapshots of the draft tree + meta), save/publish status,
 * templates, site settings (nav / logo / header / footer) and the media library.
 *
 * Every tree mutation goes through `commit(mutator)` so it is recorded ONCE in history and marks
 * the page dirty; view code never mutates state directly. Pure tree helpers live in ../schema.js.
 */
import { create } from "zustand";
import * as api from "../api";
import {
  newBlock, newSection, newPage, normalizePage, normalizeSlug, mapLists, listAt, findBlock, samePath, uid,
} from "../schema";

const HISTORY_MAX = 100;
const clone = (v) => JSON.parse(JSON.stringify(v));
const snapshotOf = (page) => clone({ sections: page.draft.sections, title: page.title, slug: page.slug, seo: page.seo });

export const useBuilder = create((set, get) => ({
  /* ── state ─────────────────────────────────────────────────────────── */
  pages: [],                 // [{id,title,slug,status,updatedAt}] (list shape from the API)
  page: null,                // the open page, full document (draft edited in place)
  sitePage: null,            // the open page of the EXISTING website ({key,title,route,group}) — edited in-place in a frame
  loading: false,
  selection: null,           // { kind: "section", sectionId } | { kind: "block", blockId, path } | null
  device: "desktop",         // desktop | tablet | mobile
  history: { past: [], future: [] },
  dirty: false,
  saving: false,
  status: "",                // short human status ("Saved", "Published", errors)
  templates: [],
  settings: null,            // { nav:{items:[]}, logo:{}, header:{}, footer:{} }
  settingsDirty: false,
  media: [],
  leftTab: "pages",          // pages | blocks | nav | media | templates | settings
  rightTab: "element",       // element | page

  /* ── pages ─────────────────────────────────────────────────────────── */
  loadPages: async () => {
    set({ loading: true });
    try { const list = await api.listPages(); set({ pages: Array.isArray(list) ? list : [] }); }
    catch (e) { set({ status: api.errorText(e, "Could not load pages") }); }
    set({ loading: false });
  },
  openPage: async (id) => {
    set({ loading: true, selection: null });
    try {
      const doc = normalizePage(await api.getPage(id));
      set({ page: doc, sitePage: null, history: { past: [], future: [] }, dirty: false, status: "", rightTab: "page" });
    } catch (e) { set({ status: api.errorText(e, "Could not open the page") }); }
    set({ loading: false });
  },
  createPage: async ({ title, slug, template } = {}) => {
    const draft = newPage({ title, slug });
    if (template) {
      const tpl = get().templates.find((t) => t.id === template);
      if (tpl) draft.draft.sections = clone(tpl.sections).map((s) => ({ ...s, id: uid("s"), blocks: reId(s.blocks) }));
      draft.template = template;
    }
    try {
      const saved = normalizePage(await api.createPage(draft));
      await get().loadPages();
      set({ page: saved, sitePage: null, history: { past: [], future: [] }, dirty: false, status: "Page created", selection: null, rightTab: "page" });
      return saved;
    } catch (e) { set({ status: api.errorText(e, "Could not create the page") }); return null; }
  },
  duplicatePage: async (id) => {
    try { const copy = normalizePage(await api.duplicatePage(id)); await get().loadPages(); set({ status: "Page duplicated" }); return copy; }
    catch (e) { set({ status: api.errorText(e, "Could not duplicate") }); return null; }
  },
  deletePage: async (id) => {
    try {
      await api.deletePage(id);
      const wasOpen = get().page && get().page.id === id;
      await get().loadPages();
      set({ status: "Page deleted", ...(wasOpen ? { page: null, selection: null, history: { past: [], future: [] }, dirty: false } : {}) });
      return true;
    } catch (e) { set({ status: api.errorText(e, "Could not delete") }); return false; }
  },

  /* ── the EXISTING website (2026-09-12) ─────────────────────────────── */
  /** open a page of the live site ({key,title,route,group} from ../sitePages) in the builder's
   *  frame. A site page and a block page are never both open: the block page, its selection and
   *  its history go away; saving belongs to the in-place editor inside the frame. */
  openSitePage: (p) => {
    if (!p || !p.route) return;
    set({
      sitePage: { key: p.key || p.route, title: p.title || p.route, route: p.route, group: p.group || "" },
      page: null, selection: null, history: { past: [], future: [] }, dirty: false, status: "", rightTab: "page",
    });
  },

  /* ── history core ──────────────────────────────────────────────────── */
  /** record the current draft, then apply `mutate(page) → page` */
  commit: (mutate, { record = true } = {}) => {
    const page = get().page;
    if (!page) return;
    const next = mutate(page);
    if (!next || next === page) return;
    const h = get().history;
    set({
      page: next,
      dirty: true,
      history: record ? { past: h.past.concat([snapshotOf(page)]).slice(-HISTORY_MAX), future: [] } : h,
    });
  },
  undo: () => {
    const { page, history } = get();
    if (!page || !history.past.length) return;
    const prev = history.past[history.past.length - 1];
    set({
      page: { ...page, draft: { sections: prev.sections }, title: prev.title, slug: prev.slug, seo: prev.seo },
      history: { past: history.past.slice(0, -1), future: history.future.concat([snapshotOf(page)]) },
      dirty: true,
    });
  },
  redo: () => {
    const { page, history } = get();
    if (!page || !history.future.length) return;
    const nxt = history.future[history.future.length - 1];
    set({
      page: { ...page, draft: { sections: nxt.sections }, title: nxt.title, slug: nxt.slug, seo: nxt.seo },
      history: { past: history.past.concat([snapshotOf(page)]).slice(-HISTORY_MAX), future: history.future.slice(0, -1) },
      dirty: true,
    });
  },
  canUndo: () => get().history.past.length > 0,
  canRedo: () => get().history.future.length > 0,

  /* ── meta ──────────────────────────────────────────────────────────── */
  updateMeta: (patch) => get().commit((p) => ({
    ...p, ...patch,
    ...(patch.slug != null ? { slug: normalizeSlug(patch.slug) } : {}),
    ...(patch.seo ? { seo: { ...p.seo, ...patch.seo } } : {}),
  })),

  /* ── sections ──────────────────────────────────────────────────────── */
  addSection: (index, section) => get().commit((p) => {
    const s = section && section.id ? section : newSection(section || {});
    const list = p.draft.sections.slice();
    list.splice(index == null ? list.length : index, 0, s);
    set({ selection: { kind: "section", sectionId: s.id } });
    return { ...p, draft: { sections: list } };
  }),
  removeSection: (sectionId) => get().commit((p) => {
    set({ selection: null });
    return { ...p, draft: { sections: p.draft.sections.filter((s) => s.id !== sectionId) } };
  }),
  moveSection: (from, to) => get().commit((p) => {
    const list = p.draft.sections.slice();
    if (from < 0 || from >= list.length || to < 0 || to >= list.length || from === to) return p;
    const [s] = list.splice(from, 1);
    list.splice(to, 0, s);
    return { ...p, draft: { sections: list } };
  }),
  updateSection: (sectionId, patch) => get().commit((p) => ({
    ...p, draft: { sections: p.draft.sections.map((s) => (s.id === sectionId ? { ...s, ...patch, style: { ...s.style, ...(patch.style || {}) } } : s)) },
  })),
  duplicateSection: (sectionId) => get().commit((p) => {
    const i = p.draft.sections.findIndex((s) => s.id === sectionId);
    if (i < 0) return p;
    const src = p.draft.sections[i];
    const copy = { ...clone(src), id: uid("s"), blocks: reId(src.blocks) };
    const list = p.draft.sections.slice();
    list.splice(i + 1, 0, copy);
    set({ selection: { kind: "section", sectionId: copy.id } });
    return { ...p, draft: { sections: list } };
  }),

  /* ── blocks ────────────────────────────────────────────────────────── */
  /** path = [sectionId] or [sectionId, columnsBlockId, colIndex, ...]; index null = append */
  addBlock: (path, index, blockOrType) => get().commit((p) => {
    const b = typeof blockOrType === "string" ? newBlock(blockOrType) : (blockOrType.id ? blockOrType : newBlock(blockOrType.type, blockOrType));
    const sections = mapLists(p.draft.sections, (list, lp) => {
      if (!samePath(lp, path)) return list;
      const out = list.slice();
      out.splice(index == null ? out.length : index, 0, b);
      return out;
    });
    set({ selection: { kind: "block", blockId: b.id, path }, rightTab: "element" });
    return { ...p, draft: { sections } };
  }),
  removeBlock: (blockId) => get().commit((p) => {
    set({ selection: null });
    return { ...p, draft: { sections: mapLists(p.draft.sections, (list) => list.filter((b) => b.id !== blockId)) } };
  }),
  updateBlock: (blockId, patch, opts) => get().commit((p) => ({
    ...p, draft: { sections: mapLists(p.draft.sections, (list) => list.map((b) => (b.id === blockId
      ? { ...b, props: { ...b.props, ...(patch.props || {}) }, style: { ...b.style, ...(patch.style || {}) } }
      : b))) },
  }), opts),
  duplicateBlock: (blockId) => get().commit((p) => {
    const hit = findBlock(p.draft.sections, blockId);
    if (!hit) return p;
    const copy = { ...clone(hit.block), id: uid("b") };
    if (copy.props && Array.isArray(copy.props.columns)) copy.props.columns = copy.props.columns.map(reId);
    const sections = mapLists(p.draft.sections, (list, lp) => {
      if (!samePath(lp, hit.path)) return list;
      const out = list.slice();
      out.splice(hit.index + 1, 0, copy);
      return out;
    });
    set({ selection: { kind: "block", blockId: copy.id, path: hit.path } });
    return { ...p, draft: { sections } };
  }),
  /** move a block to `toPath` at `toIndex` (index in the list BEFORE removal semantics handled here) */
  moveBlock: (blockId, toPath, toIndex) => get().commit((p) => {
    const hit = findBlock(p.draft.sections, blockId);
    if (!hit) return p;
    if (!listAt(p.draft.sections, toPath)) return p;
    // a block cannot be dropped inside itself
    if (toPath.includes(blockId)) return p;
    let target = toIndex == null ? Infinity : toIndex;
    if (samePath(hit.path, toPath) && hit.index < target) target -= 1;
    const without = mapLists(p.draft.sections, (list) => list.filter((b) => b.id !== blockId));
    const sections = mapLists(without, (list, lp) => {
      if (!samePath(lp, toPath)) return list;
      const out = list.slice();
      out.splice(Math.min(target, out.length), 0, hit.block);
      return out;
    });
    set({ selection: { kind: "block", blockId, path: toPath } });
    return { ...p, draft: { sections } };
  }),

  /* ── selection / ui ────────────────────────────────────────────────── */
  select: (selection) => set({ selection, rightTab: selection ? "element" : "page" }),
  setDevice: (device) => set({ device }),
  setLeftTab: (leftTab) => set({ leftTab }),
  setRightTab: (rightTab) => set({ rightTab }),
  setStatus: (status) => set({ status }),

  /* ── persistence ───────────────────────────────────────────────────── */
  saveDraft: async () => {
    const page = get().page;
    if (!page || !page.id) return false;
    set({ saving: true });
    try {
      const saved = normalizePage(await api.savePage(page.id, {
        title: page.title, slug: page.slug, seo: page.seo, template: page.template, order: page.order, draft: page.draft,
      }));
      set({ page: { ...page, updatedAt: saved.updatedAt, slug: saved.slug }, dirty: false, status: "Draft saved" });
      get().loadPages();
      set({ saving: false });
      return true;
    } catch (e) { set({ saving: false, status: api.errorText(e, "Could not save") }); return false; }
  },
  publish: async () => {
    const page = get().page;
    if (!page || !page.id) return false;
    if (get().dirty && !(await get().saveDraft())) return false;
    set({ saving: true });
    try {
      const doc = normalizePage(await api.publishPage(page.id));
      set({ page: { ...get().page, status: doc.status, published: doc.published }, status: "Published — live on the site", saving: false });
      get().loadPages();
      return true;
    } catch (e) { set({ saving: false, status: api.errorText(e, "Could not publish") }); return false; }
  },
  unpublish: async () => {
    const page = get().page;
    if (!page || !page.id) return false;
    try {
      const doc = normalizePage(await api.unpublishPage(page.id));
      set({ page: { ...page, status: doc.status, published: doc.published }, status: "Unpublished" });
      get().loadPages();
      return true;
    } catch (e) { set({ status: api.errorText(e, "Could not unpublish") }); return false; }
  },

  /* ── templates ─────────────────────────────────────────────────────── */
  loadTemplates: async () => { try { const t = await api.listTemplates(); set({ templates: Array.isArray(t) ? t : [] }); } catch (e) { /* optional */ } },
  saveAsTemplate: async (name) => {
    const page = get().page;
    if (!page) return null;
    try {
      const tpl = await api.createTemplate({ name: name || page.title, sections: page.draft.sections });
      await get().loadTemplates();
      set({ status: "Template saved" });
      return tpl;
    } catch (e) { set({ status: api.errorText(e, "Could not save the template") }); return null; }
  },
  applyTemplate: (templateId) => {
    const tpl = get().templates.find((t) => t.id === templateId);
    if (!tpl) return;
    get().commit((p) => ({ ...p, template: templateId, draft: { sections: clone(tpl.sections).map((s) => ({ ...s, id: uid("s"), blocks: reId(s.blocks) })) } }));
    set({ selection: null });
  },
  deleteTemplate: async (id) => { try { await api.deleteTemplate(id); await get().loadTemplates(); } catch (e) { set({ status: api.errorText(e) }); } },

  /* ── site settings (nav / logo / header / footer) ──────────────────── */
  loadSettings: async () => {
    try { const s = await api.getSettings(); set({ settings: withSettingsDefaults(s), settingsDirty: false }); }
    catch (e) { set({ settings: withSettingsDefaults(null), status: api.errorText(e, "Could not load site settings") }); }
  },
  updateSettings: (patch) => set({ settings: { ...withSettingsDefaults(get().settings), ...patch }, settingsDirty: true }),
  saveSettings: async () => {
    try { const s = await api.saveSettings(get().settings); set({ settings: withSettingsDefaults(s), settingsDirty: false, status: "Site settings saved — live" }); return true; }
    catch (e) { set({ status: api.errorText(e, "Could not save settings") }); return false; }
  },

  /* ── media ─────────────────────────────────────────────────────────── */
  loadMedia: async () => { try { const m = await api.listMedia(); set({ media: Array.isArray(m) ? m : [] }); } catch (e) { set({ status: api.errorText(e, "Could not load media") }); } },
  uploadMedia: async (file) => {
    try { const r = await api.uploadMedia(file); await get().loadMedia(); return r && r.url; }
    catch (e) { set({ status: api.errorText(e, "Upload failed") }); return null; }
  },
  deleteMedia: async (name) => { try { await api.deleteMedia(name); await get().loadMedia(); } catch (e) { set({ status: api.errorText(e, "Could not delete") }); } },
}));

/** fresh ids for a copied block list (nested columns included) */
export function reId(blocks) {
  return (blocks || []).map((b) => {
    const out = { ...clone(b), id: uid("b") };
    if (out.props && Array.isArray(out.props.columns)) out.props.columns = out.props.columns.map(reId);
    return out;
  });
}

export const NAV_ITEM = () => ({ id: uid("n"), label: "New item", href: "/", children: [] });

export function withSettingsDefaults(s) {
  const base = s && typeof s === "object" ? s : {};
  return {
    nav: { enabled: false, items: [], ...(base.nav || {}) },
    /* logo.text stays EMPTY by default: the chrome only swaps its built-in logo when the
       builder explicitly sets an image or a text (an "LHC" default here would replace the
       site's own logo markup the moment any settings doc is saved) */
    logo: { text: "", image: "", href: "/", ...(base.logo || {}) },
    header: { sticky: true, transparentOnHome: true, showSearch: true, showLanguages: true, ...(base.header || {}) },
    footer: { enabled: false, columns: [], bottomText: "", ...(base.footer || {}) },
  };
}

/** the block currently selected (or null) — convenience for the inspector */
export function selectedBlock(state) {
  if (!state.page || !state.selection || state.selection.kind !== "block") return null;
  const hit = findBlock(state.page.draft.sections, state.selection.blockId);
  return hit ? hit.block : null;
}
export function selectedSection(state) {
  if (!state.page || !state.selection) return null;
  const id = state.selection.kind === "section" ? state.selection.sectionId : (state.selection.path && state.selection.path[0]);
  return state.page.draft.sections.find((s) => s.id === id) || null;
}
